import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchOrders, syncOrders } from '../../api/orders';
import { useLatestSyncJob, waitForSyncJob } from '../../api/syncJobs';
import { invalidateCache, useCachedResource } from '../../hooks/useCachedResource';
import { notifyShippingAlertsRefresh } from './shippingAlertsRefresh';
import { useNow } from './useNow';
import {
  Badge,
  EmptyState,
  PageHeader,
  SyncRunBanner,
  StatusBanner,
  PageLoading,
  inputClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
} from '../../components/ui';
import { OrderLineProducts } from '../../components/ui/OrderLineProducts';
import { SellerCentralOrderDate } from './orderDateDisplay';
import { OrderStatusBadge } from './orderStatus';

const PAGE_SIZE = 25;

function OrdersPage({ token, user }) {
  const now = useNow();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    marketplace: '',
    status: '',
    search: '',
    days: '90',
  });
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState({ type: 'ready', message: '' });
  const [syncing, setSyncing] = useState(false);
  const {
    job: lastSyncJob,
    loading: lastSyncLoading,
    setJob: setLastSyncJob,
  } = useLatestSyncJob(token, 'orders');

  const cacheKey = useMemo(
    () =>
      `orders|${page}|${filters.days || '90'}|${filters.marketplace || ''}|${filters.status || ''}|${filters.search.trim()}|${PAGE_SIZE}`,
    [page, filters.days, filters.marketplace, filters.status, filters.search]
  );

  const fetcher = useCallback(async () => {
    const params = {
      page: String(page),
      pageSize: String(PAGE_SIZE),
      days: filters.days || '90',
    };
    if (filters.marketplace) params.marketplace = filters.marketplace;
    if (filters.status) params.status = filters.status;
    if (filters.search.trim()) params.search = filters.search.trim();
    return fetchOrders(token, params);
  }, [token, page, filters.days, filters.marketplace, filters.status, filters.search]);

  const { data, loading, refreshing, reload, error } = useCachedResource(
    token ? cacheKey : null,
    fetcher,
    { staleTime: 90_000 }
  );

  const orders = data?.orders || [];
  const pagination = data?.pagination || {
    page,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  useEffect(() => {
    // Never clear sync loading toasts — that dismisses Toastify and spawns a new popup each poll.
    if (syncing) {
      return;
    }
    if (loading && !orders.length) {
      setStatus({ type: 'loading', message: '' });
      return;
    }
    if (error && !orders.length) {
      setStatus({ type: 'error', message: error.message || 'Unable to load orders.' });
      return;
    }
    if (data && status.type === 'loading' && !status.message) {
      setStatus({ type: 'ready', message: '' });
    }
  }, [loading, error, data, orders.length, status.type, status.message, syncing]);

  const loadOrders = useCallback(
    async ({ force = false } = {}) => {
      try {
        await reload({ force, silent: Boolean(data?.orders?.length) && !force });
      } catch {
        // surfaced via error/status effect
      }
    },
    [reload, data]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((current) => {
        if (current.search === searchInput) {
          return current;
        }
        setPage(1);
        return { ...current, search: searchInput };
      });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const handleSync = async () => {
    setSyncing(true);
    setStatus({
      type: 'loading',
      message: 'Starting Amazon order sync in the background…',
    });

    try {
      const started = await syncOrders(token, { unshippedOnly: false });
      const jobId = started.jobId || started.job?.id;
      if (!jobId) {
        throw new Error(started.message || 'Unable to start order sync.');
      }

      if (started.job) {
        setLastSyncJob(started.job);
      }

      const job = await waitForSyncJob(token, jobId, {
        onUpdate: (current) => {
          setLastSyncJob(current);
          // Stable message so StatusBanner does not recreate toasts on every poll.
          setStatus((prev) => {
            const message = 'Sync running in the background…';
            if (prev?.type === 'loading' && prev.message === message) {
              return prev;
            }
            return { type: 'loading', message };
          });
        },
      });

      setLastSyncJob(job);

      if (job.status === 'failed') {
        setStatus({ type: 'error', message: job.message || job.error || 'Sync failed.' });
        return;
      }

      const warningCount = Array.isArray(job.result?.piiWarnings)
        ? job.result.piiWarnings.length
        : 0;
      const piiLoaded = job.result?.piiLoadedCount ?? 0;

      setStatus({
        type: warningCount ? 'error' : 'success',
        message:
          (job.message || `Synced ${job.result?.synced ?? 0} orders.`) +
          (piiLoaded ? ` Customer details loaded for ${piiLoaded} order(s).` : '') +
          (warningCount
            ? ` ${warningCount} order(s) missing buyer name/phone — enable PII roles on your SP-API app, then sync again.`
            : ''),
      });

      invalidateCache('orders|');
      invalidateCache('orders-unshipped|');
      notifyShippingAlertsRefresh();
      if (page !== 1) {
        setPage(1);
      } else {
        await reload({ force: true });
      }
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setSyncing(false);
    }
  };

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const total = pagination.total || 0;
  const totalPages = pagination.totalPages || 0;
  const rangeStart = total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
  const rangeEnd = Math.min(pagination.page * pagination.pageSize, total);

  return (
    <section className="grid gap-5">
      <PageHeader
        title="Orders"
        subtitle="Shipped and completed Amazon India orders. Open orders live under Unshipped."
        actions={
          <>
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => loadOrders({ force: true })}
              disabled={loading || refreshing || syncing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              type="button"
              className={primaryButtonClassName}
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync from Amazon'}
            </button>
          </>
        }
      />

      <SyncRunBanner job={lastSyncJob} loading={lastSyncLoading} />

      <StatusBanner status={status} setStatus={setStatus} />

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
        <label className="grid gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Date range
          </span>
          <select
            className={inputClassName}
            value={filters.days}
            onChange={(event) => updateFilter('days', event.target.value)}
          >
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 180 days</option>
            <option value="all">All time</option>
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Marketplace
          </span>
          <select
            className={inputClassName}
            value={filters.marketplace}
            onChange={(event) => updateFilter('marketplace', event.target.value)}
          >
            <option value="">All</option>
            <option value="IN">India</option>
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Status
          </span>
          <select
            className={inputClassName}
            value={filters.status}
            onChange={(event) => updateFilter('status', event.target.value)}
          >
            <option value="">All shipped</option>
            <option value="shipped">Shipped (in transit)</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>
        <label className="grid gap-1.5 md:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Search
          </span>
          <input
            className={inputClassName}
            type="search"
            placeholder="Order ID, buyer, city, product, ASIN, SKU..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>
      </div>

      <p className="text-sm text-slate-600">
        Status is synced from Amazon. Hover a badge for details.{' '}
        <strong className="font-semibold text-slate-800">Delivered</strong> means the expected
        delivery date has passed. Returns are not in order sync — use Seller Central.
      </p>

      {loading && !orders.length ? (
        <PageLoading label="Loading orders…" />
      ) : orders.length ? (
        <div className="grid gap-3">
          <div className="overflow-x-auto rounded-[10px] border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[76rem] border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Order date
                  </th>
                  <th className="whitespace-nowrap border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Order
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Product
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Marketplace
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Customer name
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Phone
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Ship by
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Expected delivery
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Pin code
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    US tracking
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Total
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td className={tableCellClassName}>
                      <SellerCentralOrderDate
                        value={order.orderDate}
                        marketplace={order.marketplace}
                        now={now}
                      />
                    </td>
                    <td className={`${tableCellClassName} whitespace-nowrap`}>
                      <strong className="font-mono text-sm font-semibold tracking-tight">
                        {order.amazonOrderId}
                      </strong>
                    </td>
                    <td className={tableCellClassName}>
                      <OrderLineProducts order={order} />
                    </td>
                    <td className={tableCellClassName}>
                      <Badge tone={order.marketplace === 'US' ? 'info' : 'default'}>
                        {order.marketplace}
                      </Badge>
                    </td>
                    <td className={tableCellClassName}>
                      <span className="font-semibold text-slate-900">{order.buyerName || '—'}</span>
                    </td>
                    <td className={tableCellClassName}>
                      <span className="text-sm text-slate-700">{order.buyerPhone || '—'}</span>
                    </td>
                    <td className={tableCellClassName}>
                      <span className="text-sm text-slate-700">
                        {order.shipByDate
                          ? new Date(order.shipByDate).toLocaleDateString('en-IN')
                          : '—'}
                      </span>
                    </td>
                    <td className={tableCellClassName}>
                      <span className="text-sm text-slate-700">
                        {order.deliveryDate
                          ? new Date(order.deliveryDate).toLocaleDateString('en-IN')
                          : '—'}
                      </span>
                    </td>
                    <td className={tableCellClassName}>
                      <span className="text-sm text-slate-700">{order.shipPincode || '—'}</span>
                    </td>
                    <td className={tableCellClassName}>
                      <span className="font-mono text-xs">{order.trackingUs || '—'}</span>
                    </td>
                    <td className={tableCellClassName}>
                      <OrderStatusBadge order={order} />
                    </td>
                    <td className={tableCellClassName}>
                      {order.currency} {Number(order.totalAmount).toFixed(2)}
                    </td>
                    <td className={tableCellClassName}>
                      <Link
                        to={`/admin/orders/${order.id}`}
                        className="font-semibold text-blue-700 hover:text-blue-800"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
            <p className="text-slate-600">
              Showing {rangeStart}–{rangeEnd} of {total.toLocaleString('en-IN')} order
              {total === 1 ? '' : 's'}
              {filters.days !== 'all' ? ` (last ${filters.days} days)` : ''}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={page <= 1 || loading || refreshing}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span className="min-w-[7rem] text-center font-medium text-slate-800">
                Page {totalPages === 0 ? 0 : page} of {totalPages}
              </span>
              <button
                type="button"
                className={secondaryButtonClassName}
                disabled={totalPages === 0 || page >= totalPages || loading || refreshing}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <EmptyState
          title="No shipped orders yet"
          description={
            filters.days !== 'all'
              ? `No shipped orders in the last ${filters.days} days. Try a wider date range, sync from Amazon, or check Unshipped.`
              : 'Open and pending orders appear under Unshipped. Sync from Amazon or ship an order to see it here.'
          }
          action={
            <Link to="/admin/unshipped-orders" className={primaryButtonClassName}>
              View unshipped orders
            </Link>
          }
        />
      )}
    </section>
  );
}

export default OrdersPage;
